/**
 * Client-side first-touch attribution capture.
 *
 * Mounted globally via `<AttributionCapture />` in `src/app/layout.tsx`.
 * On every page-load mount, runs once:
 *
 *   1. If __feelgood_attribution cookie already exists → exit (first-touch wins).
 *   2. Read URL params for allowlisted keys (utm_*, fbclid, gclid, _ef_transaction_id, clickid, affid).
 *   3. Capture document.referrer + window.location.pathname + ISO timestamp.
 *   4. If nothing meaningful captured (no UTM, no click ID, no external referrer) → exit.
 *      Internal navs shouldn't pollute the cookie store.
 *   5. Write the cookie: 60-day Max-Age, Domain=.rxhealthtoday.com, Path=/, SameSite=Lax.
 *      NOT HttpOnly — A2 surfaces (FB Pixel companion, future inline scripts) need to read it.
 *
 * The cookie payload runs through the same allowlist `sanitizeAttribution`
 * the server uses, so no auth artifact / PII can land in the cookie via
 * URL injection.
 */

import {
  COOKIE_NAME,
  MARKETING_SESSION_COOKIE_NAME,
  COOKIE_MAX_AGE_SECONDS,
  COOKIE_DOMAIN,
  ALLOWED_ATTRIBUTION_KEYS,
  sanitizeAttribution,
} from './attribution';

export function captureAttributionFromUrl(): void {
  if (typeof window === 'undefined') return;

  // First-touch wins. Don't overwrite a cookie set on an earlier visit.
  if (document.cookie.split(';').some((c) => c.trim().startsWith(`${COOKIE_NAME}=`))) {
    return;
  }

  const url = new URL(window.location.href);
  const captured: Record<string, string> = {};

  for (const key of ALLOWED_ATTRIBUTION_KEYS) {
    const v = url.searchParams.get(key);
    if (v) captured[key] = v;
  }

  // Referrer + landing path are derived, never user-supplied via URL.
  // The trailing slice cap mirrors the server-side sanitizer's 512-char cap
  // (defensive — a referrer URL longer than 512 is suspicious).
  if (document.referrer) captured.referrer = document.referrer.slice(0, 512);
  captured.landing_path = url.pathname;
  captured.captured_at = new Date().toISOString();

  // Skip if no marketing signal captured. An external referrer alone
  // counts (organic-search arrivals, partner blog links) — even without
  // UTM. Internal navs (referrer matches our host) don't.
  //
  // NOTE: per LESSON 2026-04-28, this predicate gates capture on a subset
  // of ALLOWED_ATTRIBUTION_KEYS. Keep it in sync with the allowlist when
  // new signal-bearing keys land — `scripts/test-cookie-network-id-expansion.ts`
  // (and the prior A4 click-param test) source-greps for drift.
  const hasUtm = !!(captured.utm_source || captured.utm_medium || captured.utm_campaign);
  const hasClickId = !!(
    captured.fbclid || captured.gclid || captured.ttclid || captured.msclkid ||
    captured._ef_transaction_id || captured.clickid || captured.affid
  );
  // Network-keyed first-touch: operator-constructed tracking URLs may carry
  // `network_id` slug, OR (more commonly per Phase 4 inbound passthrough) any
  // of the network-shape sub-aff/offer/uid slots. Either signals "this is
  // attributable network traffic" — capture so the resolver downstream can
  // bind to a network via slug or via link_defaults reverse-lookup.
  const hasNetwork = !!(
    captured.network_id ||
    captured.pub || captured.sub1 || captured.sub2 || captured.sub3 ||
    captured.sub4 || captured.sub5 ||
    captured.oid || captured.oid2 || captured.oid3 || captured.oid4 ||
    captured.uid ||
    captured.affid2 || captured.affid3 || captured.affid4
  );
  const hasExternalReferrer = !!captured.referrer
    && !captured.referrer.includes(window.location.host);

  if (!hasUtm && !hasClickId && !hasNetwork && !hasExternalReferrer) return;

  // Run through the same allowlist the server uses. Anything we somehow
  // populated outside the allowlist gets dropped here — defense-in-depth.
  const payload = sanitizeAttribution(captured);
  if (Object.keys(payload).length === 0) return;

  const value = encodeURIComponent(JSON.stringify(payload));
  document.cookie =
    `${COOKIE_NAME}=${value}; Domain=${COOKIE_DOMAIN}; Path=/; ` +
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;

  // Companion marketing-session cookie — uuid for cookie-first dedup
  // in `findOrCreateMarketingEventLog`. Set when missing (defense in
  // depth: a previously-evicted attribution cookie that left this one
  // behind shouldn't get a fresh session id). `crypto.randomUUID` has
  // shipped in every modern browser since 2021 — Brave/Safari/Firefox/
  // Chrome/Edge all support it.
  const hasSessionCookie = document.cookie
    .split(';')
    .some((c) => c.trim().startsWith(`${MARKETING_SESSION_COOKIE_NAME}=`));
  if (!hasSessionCookie) {
    const sessionId = window.crypto.randomUUID();
    document.cookie =
      `${MARKETING_SESSION_COOKIE_NAME}=${sessionId}; Domain=${COOKIE_DOMAIN}; Path=/; ` +
      `Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
  }
}
